import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FiMenu, FiX, FiChevronDown, FiChevronRight, FiFacebook, FiTwitter, FiLinkedin, FiInstagram, FiYoutube } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { useSettings } from '../contexts/SettingsContext';

// Navigation menu structure
const navigationMenu = [
  { name: 'Home', path: '/', dropdown: false },
  { name: 'About', path: '/about', dropdown: false },
  { name: 'Courses', path: '/courses', dropdown: true },
  { name: 'Blog', path: '/blog', dropdown: false },
  { name: 'Contact', path: '/contact', dropdown: false }
];

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  /* Use opaque background so the bar is always visible over content */
  background: rgba(255, 255, 255, 1);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  z-index: 2000; /* Ensure always above page content */
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${props => props.scrolled ? '0 4px 20px rgba(0, 0, 0, 0.08)' : '0 2px 10px rgba(0, 0, 0, 0.04)'};
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;
  /* Tighter vertical padding to reduce space above/below the logo */
  padding: ${props => props.scrolled ? '0.35rem 0' : '0.9rem 0'};
  min-height: ${props => props.scrolled ? '56px' : '68px'};
  /* Align with page content container (see Home.Container max-width: 1400px) */
  max-width: 1400px;
  margin: 0 auto;
  /* Match page container horizontal padding */
  padding-left: ${props => props.theme.spacing.xl};
  padding-right: ${props => props.theme.spacing.xl};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding-left: ${props => props.theme.spacing.md};
    padding-right: ${props => props.theme.spacing.md};
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  font-family: ${props => props.theme.fonts.primary};
  font-size: ${props => props.theme.fontSizes.xl};
  font-weight: ${props => props.theme.fontWeights.bold};
  color: ${props => props.theme.colors.dark};
  text-decoration: none;
  transition: all 0.3s ease;
  /* Shift logo slightly left on wide screens as requested */
  margin-left: calc(-1 * ${props => props.theme.spacing.lg});
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    margin-left: 0;
  }
  
  &:hover {
    transform: scale(1.02);
    /* Override global anchor hover (red) so the 'Service' text turns blue on hover */
    color: ${props => props.theme.colors.primary};
  }

  .logo-image {
    /* Responsive logo image; shrink more when compact */
    width: ${props => props.$compact ? 'clamp(44px, 5.2vw, 64px)' : 'clamp(56px, 6.5vw, 80px)'};
    height: ${props => props.$compact ? 'clamp(44px, 5.2vw, 64px)' : 'clamp(56px, 6.5vw, 80px)'};
    object-fit: contain;
    margin-right: ${props => props.theme.spacing.sm};
    border-radius: ${props => props.theme.borderRadius.md};
  }

  .logo-text {
    display: flex;
    flex-direction: column;
    line-height: 1.2;
    
    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      font-size: ${props => props.theme.fontSizes.lg};
    }
  }

  .logo-main {
    /* Dial back brand text size; smaller when compact */
    font-size: ${props => props.$compact 
      ? 'clamp(1rem, 1.6vw, 1.25rem)'
      : `clamp(${props => props.theme.fontSizes.lg}, 1.8vw, ${props => props.theme.fontSizes['2xl']})`};
  }

  .logo-sub {
    /* Subtext smaller; hide when compact */
    font-size: clamp(${props => props.theme.fontSizes.sm}, 1vw, ${props => props.theme.fontSizes.base});
    color: ${props => props.theme.colors.gray[600]};
    font-weight: ${props => props.theme.fontWeights.medium};
    margin-top: -2px;
    display: ${props => props.$compact ? 'none' : 'block'};
  }

  .logo-highlight {
    color: #ff2600; /* Specific brand color for 'Hub' */
  }
`;

const DesktopMenu = styled.ul`
  display: flex;
  align-items: center;
  list-style: none;
  margin: 0;
  padding: 0;

  /* Slightly tighter layout on mid-width desktops to prevent crowding */
  @media (max-width: 900px) and (min-width: ${props => parseInt(props.theme.breakpoints.tablet) + 1}px) {
    gap: ${props => props.theme.spacing.lg};
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: none;
  }
`;

const RightActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.lg};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    gap: ${props => props.theme.spacing.md};
  }

  /* Hide right actions on narrow desktop widths to keep header clean */
  @media (max-width: 900px) and (min-width: ${props => parseInt(props.theme.breakpoints.tablet) + 1}px) {
    display: none;
  }
`;

const SocialLinksHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};

  a {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: ${props => props.theme.borderRadius.full};
    background: ${props => props.scrolled ? 'rgba(0,0,0,0.04)' : 'rgba(0,0,0,0.03)'};
    color: ${props => props.theme.colors.dark};
    text-decoration: none;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    /* Brand-specific hover colors */
    &.facebook:hover {
      background: #1877F2;
      color: white;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(24, 119, 242, 0.3);
    }

    &.twitter:hover {
      background: #1DA1F2;
      color: white;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(29, 161, 242, 0.3);
    }

    &.linkedin:hover {
      background: #0A66C2;
      color: white;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(10, 102, 194, 0.3);
    }

    &.instagram:hover {
      background: linear-gradient(45deg, #F58529, #DD2A7B, #8134AF, #515BD4);
      color: white;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(225, 48, 108, 0.3);
    }

    &.youtube:hover {
      background: #FF0000;
      color: white;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(255, 0, 0, 0.3);
    }

    &.whatsapp:hover {
      background: #25D366;
      color: white;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(37, 211, 102, 0.3);
    }

    svg { font-size: ${props => props.theme.fontSizes.xl}; }
  }

  /* Hide social icons on small screens to keep header clean */
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: none;
  }
`;

const MenuItem = styled.li`
  position: relative;
  margin-left: ${props => props.theme.spacing.xl};

  &:first-child {
    margin-left: 0;
  }

  @media (max-width: 900px) and (min-width: ${props => parseInt(props.theme.breakpoints.tablet) + 1}px) {
    margin-left: ${props => props.theme.spacing.lg};
  }
`;

const MenuLink = styled(Link)`
  display: flex;
  align-items: center;
  color: ${props => props.$active ? props.theme.colors.primary : props.theme.colors.dark};
  font-weight: ${props => props.$active ? props.theme.fontWeights.semibold : props.theme.fontWeights.medium};
  font-size: ${props => props.$compact ? props.theme.fontSizes.base : props.theme.fontSizes.lg};
  text-decoration: none;
  /* Reduce vertical padding on links to keep header compact */
  padding: ${props => props.$compact ? '0.35rem 0' : props.theme.spacing.sm + ' 0'};
  transition: ${props => props.theme.transitions.default};

  &:hover {
    color: ${props => props.theme.colors.secondary};
  }

  svg {
    margin-left: ${props => props.theme.spacing.xs};
    transition: ${props => props.theme.transitions.default};
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: ${props => props.theme.fontSizes.base};
    padding: ${props => props.theme.spacing.sm} 0;
  }

  /* Slightly smaller nav text on narrow desktop widths */
  @media (max-width: 900px) and (min-width: ${props => parseInt(props.theme.breakpoints.tablet) + 1}px) {
    font-size: ${props => props.theme.fontSizes.base};
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background: ${props => props.theme.colors.light};
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: ${props => props.theme.shadows.xl};
  opacity: ${props => props.show ? 1 : 0};
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  transform: ${props => props.show ? 'translateY(0)' : 'translateY(-10px)'};
  transition: ${props => props.theme.transitions.default};
  min-width: 220px;
  padding: ${props => props.theme.spacing.md} 0;
  margin-top: ${props => props.theme.spacing.sm};
`;

const DropdownItem = styled(Link)`
  display: block;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.dark};
  text-decoration: none;
  transition: ${props => props.theme.transitions.default};

  &:hover {
    background: ${props => props.theme.colors.gray[100]};
    color: ${props => props.theme.colors.primary};
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: ${props => props.theme.fontSizes['2xl']};
  color: ${props => props.theme.colors.dark};
  cursor: pointer;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: block;
  }
`;

const MobileMenuOverlay = styled.div`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.65);
  opacity: ${props => props.show ? 1 : 0};
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  z-index: 9998;
  pointer-events: ${props => props.show ? 'auto' : 'none'};
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: block;
  }
`;

const MobileMenu = styled.div`
  display: none;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 280px;
  max-width: 85vw;
  background: #ffffff;
  box-shadow: ${props => props.show ? '-12px 0 30px rgba(0, 0, 0, 0.12)' : 'none'};
  transform: ${props => props.show ? 'translateX(0)' : 'translateX(calc(100% + 2px))'};
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 9999;
  overflow-y: auto;
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  pointer-events: ${props => props.show ? 'auto' : 'none'};
  will-change: transform;
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: flex;
    flex-direction: column;
  }
`;

const MobileMenuHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  padding: ${props => props.theme.spacing.lg};
  border-bottom: 1px solid #e5e7eb;
  background: #fafafa;
  flex-shrink: 0;
`;

const MobileMenuContent = styled.div`
  flex: 1;
  width: 100%;
  padding: ${props => props.theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
  overflow-y: auto;
`;

const MobileMenuItem = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const MobileMenuLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  color: ${props => props.$active ? props.theme.colors.primary : '#1f2937'};
  font-size: ${props => props.theme.fontSizes.lg};
  font-weight: ${props => props.$active ? props.theme.fontWeights.bold : props.theme.fontWeights.medium};
  text-decoration: none;
  padding: ${props => props.theme.spacing.md};
  width: 100%;
  background: ${props => props.$active ? 'rgba(255, 68, 68, 0.1)' : 'transparent'};
  border-radius: ${props => props.theme.borderRadius.md};
  transition: all 0.2s ease;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
    background: rgba(255, 68, 68, 0.05);
  }
`;

const MobileDropdownToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${props => props.theme.spacing.md};
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.$expanded ? 'rgba(255, 68, 68, 0.1)' : 'transparent'};
  color: ${props => props.$expanded ? props.theme.colors.primary : '#1f2937'};
  font-size: ${props => props.theme.fontSizes.lg};
  font-family: inherit;
  font-weight: ${props => props.$expanded ? props.theme.fontWeights.bold : props.theme.fontWeights.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
    background: rgba(255, 68, 68, 0.05);
  }
`;

const ChevronIcon = styled(FiChevronRight)`
  transition: transform 0.25s ease;
  transform: ${props => props.$expanded ? 'rotate(90deg)' : 'rotate(0deg)'};
`;

const MobileDropdownGroup = styled.div`
  overflow: hidden;
  max-height: ${props => props.$expanded ? '400px' : '0'};
  transition: max-height 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
  margin-top: ${props => props.$expanded ? props.theme.spacing.sm : '0'};
`;

const MobileCourseItem = styled(Link)`
  display: flex;
  align-items: center;
  color: #6b7280;
  font-size: ${props => props.theme.fontSizes.base};
  font-weight: ${props => props.theme.fontWeights.medium};
  text-decoration: none;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  padding-left: ${props => props.theme.spacing['2xl']};
  width: 100%;
  background: transparent;
  border-radius: ${props => props.theme.borderRadius.sm};
  transition: all 0.2s ease;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
    background: rgba(255, 68, 68, 0.05);
  }
`;

const CloseButton = styled.button`
  background: #f3f4f6;
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #6b7280;

  &:hover {
    background: #e5e7eb;
    color: ${props => props.theme.colors.primary};
  }

  svg {
    font-size: ${props => props.theme.fontSizes.lg};
  }
`;

const Header = () => {
  const headerRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [expandedMobileMenu, setExpandedMobileMenu] = useState(null);
  const [courses, setCourses] = useState([]);
  const location = useLocation();
  const { settings } = useSettings();
  const TABLET_BREAKPOINT = 768; // sync with theme.breakpoints.tablet

  useEffect(() => {
    const handleScroll = () => {
  const next = window.scrollY > 50;
  setScrolled(next);
      // Update CSS var for header height on scroll (height can change when shrunk)
      if (headerRef.current) {
        const h = headerRef.current.getBoundingClientRect().height;
        document.documentElement.style.setProperty('--header-offset', `${Math.round(h)}px`);
      }
    };

    window.addEventListener('scroll', handleScroll);
    // Initial set
    const setInitialOffset = () => {
      if (headerRef.current) {
        const h = headerRef.current.getBoundingClientRect().height;
        document.documentElement.style.setProperty('--header-offset', `${Math.round(h)}px`);
      }
    };
    setInitialOffset();
    // Recheck after layout/asset load for robustness
    requestAnimationFrame(setInitialOffset);
    window.addEventListener('load', setInitialOffset, { once: true });

    const handleResize = () => {
      if (headerRef.current) {
        const h = headerRef.current.getBoundingClientRect().height;
        document.documentElement.style.setProperty('--header-offset', `${Math.round(h)}px`);
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('load', setInitialOffset);
    };
  }, []);

  useEffect(() => {
    // Load course menu items from settings context
    if (settings?.navigation?.courseDropdownItems) {
      setCourses(settings.navigation.courseDropdownItems);
    }
  }, [settings]);

  // Auto-close mobile menu when resizing to tablet/desktop and on route changes
  useEffect(() => {
    const handleResizeClose = () => {
      if (window.innerWidth >= TABLET_BREAKPOINT && showMobileMenu) {
        setShowMobileMenu(false);
        setExpandedMobileMenu(null);
      }
    };

    window.addEventListener('resize', handleResizeClose);
    // Run once to sync when devtools resizes viewport
    handleResizeClose();
    return () => window.removeEventListener('resize', handleResizeClose);
  }, [showMobileMenu]);

  useEffect(() => {
    // Close on route change for safety
    setShowMobileMenu(false);
    setExpandedMobileMenu(null);
  }, [location.pathname]);

  // Extra safety: listen to matchMedia changes to close menu precisely at breakpoint
  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${TABLET_BREAKPOINT + 1}px)`);
    const onChange = (e) => {
      if (e.matches) {
        setShowMobileMenu(false);
        setExpandedMobileMenu(null);
      }
    };
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    // Sync once initially
    onChange(mq);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setShowMobileMenu(false);
      }
    };

    if (showMobileMenu) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [showMobileMenu]);

  // No debug side-effects

  // Filter menu items based on settings
  const menuItems = navigationMenu.filter(item => {
    // Hide blog if disabled in settings
    if (item.path === '/blog') {
      const blogEnabled = settings?.features?.blogEnabled ?? true;
      if (blogEnabled === false) {
        return false;
      }
    }
    return true;
  });

  // Build social links from settings (same mapping as Footer)
  const formatWhatsAppUrl = (raw) => {
    if (!raw) return '';
    const number = String(raw).replace(/[^\d]/g, '');
    return number ? `https://wa.me/${number}` : '';
  };

  const socialLinks = [
    { icon: FiFacebook, url: settings?.social?.facebook, name: 'Facebook' },
    { icon: FiTwitter, url: settings?.social?.twitter, name: 'Twitter' },
    { icon: FiLinkedin, url: settings?.social?.linkedin, name: 'LinkedIn' },
    { icon: FiInstagram, url: settings?.social?.instagram, name: 'Instagram' },
    { icon: FiYoutube, url: settings?.social?.youtube, name: 'YouTube' },
    { icon: FaWhatsapp, url: formatWhatsAppUrl(settings?.social?.whatsapp), name: 'WhatsApp' },
  ].filter(l => !!l.url);

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
  <HeaderContainer scrolled={scrolled} ref={headerRef}>
        <Nav scrolled={scrolled}>
          <Logo to="/" $compact={scrolled}>
            <img src="/logo.png" alt="Service Hub IT Solutions" className="logo-image" />
            <div className="logo-text">
              <div className="logo-main">
                Service <span className="logo-highlight">Hub</span>
              </div>
              <div className="logo-sub">IT Solutions</div>
            </div>
          </Logo>

          <DesktopMenu>
            {menuItems.map((item) => (
              <MenuItem
                key={item.name}
                onMouseEnter={() => item.dropdown && setShowDropdown(true)}
                onMouseLeave={() => item.dropdown && setShowDropdown(false)}
              >
                <MenuLink
                  to={item.path}
                  $compact={scrolled}
                  $active={isActive(item.path)}
                >
                  {item.name}
                  {item.dropdown && <FiChevronDown />}
                </MenuLink>
                
                {item.dropdown && (
                  <Dropdown show={showDropdown}>
                    {courses.map((course) => (
                      <DropdownItem key={course.name} to={course.path}>
                        {course.name}
                      </DropdownItem>
                    ))}
                  </Dropdown>
                )}
              </MenuItem>
            ))}
          </DesktopMenu>

          <RightActions>
            <SocialLinksHeader scrolled={scrolled}>
              {socialLinks.map(({ icon: Icon, url, name }) => (
                <a 
                  key={name} 
                  href={url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  aria-label={name}
                  className={name.toLowerCase()}
                >
                  <Icon />
                </a>
              ))}
            </SocialLinksHeader>

            <MobileMenuButton onClick={() => setShowMobileMenu(true)} aria-label="Open menu">
              <FiMenu />
            </MobileMenuButton>
          </RightActions>
        </Nav>
      </HeaderContainer>

      <MobileMenuOverlay 
        show={showMobileMenu} 
        onClick={() => setShowMobileMenu(false)} 
      />

      <MobileMenu show={showMobileMenu}>
        <MobileMenuHeader>
          <CloseButton onClick={() => setShowMobileMenu(false)}>
            <FiX />
          </CloseButton>
        </MobileMenuHeader>

        <MobileMenuContent>
          {menuItems.map((item) => (
            <MobileMenuItem key={item.name}>
              {item.dropdown ? (
                <>
                  <MobileDropdownToggle
                    type="button"
                    $expanded={expandedMobileMenu === item.name}
                    onClick={() => setExpandedMobileMenu((prev) => prev === item.name ? null : item.name)}
                  >
                    <span>{item.name}</span>
                    <ChevronIcon $expanded={expandedMobileMenu === item.name} />
                  </MobileDropdownToggle>

                  <MobileDropdownGroup $expanded={expandedMobileMenu === item.name}>
                    {courses.map((course) => (
                      <MobileCourseItem
                        key={course.name}
                        to={course.path}
                        onClick={() => {
                          setShowMobileMenu(false);
                          setExpandedMobileMenu(null);
                        }}
                      >
                        {course.name}
                      </MobileCourseItem>
                    ))}
                    <MobileCourseItem
                      to={item.path}
                      onClick={() => {
                        setShowMobileMenu(false);
                        setExpandedMobileMenu(null);
                      }}
                    >
                      View All Courses
                    </MobileCourseItem>
                  </MobileDropdownGroup>
                </>
              ) : (
                <MobileMenuLink
                  to={item.path}
                  $active={isActive(item.path)}
                  onClick={() => setShowMobileMenu(false)}
                >
                  <span>{item.name}</span>
                </MobileMenuLink>
              )}
            </MobileMenuItem>
          ))}
        </MobileMenuContent>
      </MobileMenu>
    </>
  );
};

export default Header;